import React, { useEffect, useMemo, useState } from 'react';
import { Avatar, Button, Form, Select, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import ResponsiveModal from '../../Shared/ResponsiveModal/ResponsiveModal';
import { getAgents } from '../../Agents/AgentServices';
import { useErrorHandler } from '../../../Hooks/useErrorHandler';

const { Text } = Typography;

const getAgentImage = (agent) => agent.picture_url || agent.avatar_url || agent.image_url || null;
const getAgentDescription = (agent) => agent.description || agent.short_description || '';
const getAgentSearchText = (agent) => [
    agent.name,
    getAgentDescription(agent),
].filter(Boolean).join(' ').toLowerCase();

const renderAgentOption = (agent) => {
    const description = getAgentDescription(agent);
    const image = getAgentImage(agent);

    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <Avatar
                size={34}
                src={image}
                style={{ flexShrink: 0, background: '#e6f4ff', color: '#1677ff' }}
            >
                {!image && (agent.name || '?').charAt(0).toUpperCase()}
            </Avatar>
            <div style={{ minWidth: 0, flex: 1 }}>
                <Text strong style={{ display: 'block', lineHeight: 1.2 }} ellipsis>
                    {agent.name}
                </Text>
                {description && (
                    <Text type="secondary" style={{ display: 'block', fontSize: 12, lineHeight: 1.25 }} ellipsis>
                        {description}
                    </Text>
                )}
            </div>
        </div>
    );
};

const ChannelAgentEditModal = ({ channel, onCancel, onUpdate, loading = false }) => {
    const { t } = useTranslation();
    const { handleError } = useErrorHandler();
    const [form] = Form.useForm();
    const [agents, setAgents] = useState([]);
    const [agentsLoading, setAgentsLoading] = useState(false);

    const currentAgentId = channel.agent_id ?? channel.agent?.id ?? null;

    useEffect(() => {
        form.setFieldsValue({ agent_id: currentAgentId });
    }, [currentAgentId, form]);

    useEffect(() => {
        const controller = new AbortController();

        const fetchAgents = async () => {
            setAgentsLoading(true);
            try {
                const response = await getAgents({
                    page: 1,
                    size: 100,
                    signal: controller.signal,
                });
                setAgents(response.data || []);
            } catch (error) {
                if (error?.name !== 'CanceledError' && error?.name !== 'AbortError') {
                    handleError(error);
                }
            } finally {
                if (!controller.signal.aborted) {
                    setAgentsLoading(false);
                }
            }
        };

        fetchAgents();

        return () => controller.abort();
    }, [handleError]);

    const agentOptions = useMemo(() => {
        const options = agents.map((agent) => ({
            value: agent.id,
            label: renderAgentOption(agent),
            displayLabel: agent.name,
            searchText: getAgentSearchText(agent),
        }));

        if (channel.agent?.id && !options.some((option) => option.value === channel.agent.id)) {
            options.unshift({
                value: channel.agent.id,
                label: renderAgentOption(channel.agent),
                displayLabel: channel.agent.name,
                searchText: getAgentSearchText(channel.agent),
            });
        }

        return options;
    }, [agents, channel.agent]);

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            const success = await onUpdate(channel.id, {
                agent_id: values.agent_id ?? null,
            }, form);

            if (success) {
                onCancel();
            }
        } catch (error) {
            if (error.errorFields) return;
            handleError(error, form);
        }
    };

    return (
        <ResponsiveModal
            title={t('edit_agent')}
            open
            onCancel={onCancel}
            desktopWidth={420}
            footer={(
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                    <Button onClick={onCancel}>
                        {t('cancel')}
                    </Button>
                    <Button type="primary" onClick={handleSubmit} loading={loading}>
                        {t('save')}
                    </Button>
                </div>
            )}
        >
            <Form form={form} layout="vertical">
                <Form.Item
                    label={t('agent')}
                    name="agent_id"
                    tooltip={t('agent_assignment_tooltip', { defaultValue: 'Optionally assign an agent to handle messages from this channel' })}
                >
                    <Select
                        placeholder={t('select_agent')}
                        loading={agentsLoading}
                        options={agentOptions}
                        optionLabelProp="displayLabel"
                        allowClear
                        showSearch
                        filterOption={(input, option) => (
                            option?.searchText?.includes(input.toLowerCase().trim())
                        )}
                    />
                </Form.Item>
            </Form>
        </ResponsiveModal>
    );
};

export default ChannelAgentEditModal;
